import { useEffect, useMemo, useRef } from 'react';
import { useImmer } from 'use-immer';
import moment from 'moment';
import { SERVER_FMT, zonedToEpoch } from '../lib/clock';
import { playableItems } from '../lib/items';
import { timelineAt, wallItemSeconds } from '../lib/timeline';

const IDLE = [];
const NO_ITEMS = [];
const NOOP = () => {};

// setTimeout can't wait longer than ~24.8 days; we re-plan well before that anyway.
const MAX_WAIT_MS = 60 * 60 * 1000;

/**
 * Playback for a video wall tile, with the same result shape as usePlayback. Instead of chaining
 * a timer per item, the slot and item showing are worked out from the synced clock (`now`) and
 * the slot's start instant, so every tile shows the same item at the same moment and a tile that
 * boots late joins in step. `itemStartedAtMs` is when the current item began on that timeline.
 *
 * Every tile must see the same list of items, so web pages aren't dropped while offline (an
 * offline tile shows them blank but keeps the timing).
 */
export const useTimelinePlayback = ({
  schedules,
  localNow,
  timeZone,
  now,
  onSlotEnded,
}) => {
  const [state, setState] = useImmer({
    activeItem: IDLE,
    index: 0,
    itemStartedAtMs: 0,
  });
  const onSlotEndedRef = useRef(onSlotEnded);
  onSlotEndedRef.current = onSlotEnded;

  const dayKey = localNow
    ? moment(localNow, SERVER_FMT).format('YYYY-MM-DD')
    : undefined;

  // The day's slots as instants on the shared clock.
  const slots = useMemo(
    () =>
      dayKey && timeZone
        ? (schedules || []).map((slot) => ({
            slot,
            startMs: zonedToEpoch(dayKey, slot.startAt, timeZone),
            endMs: zonedToEpoch(dayKey, slot.endAt, timeZone),
          }))
        : [],
    [schedules, dayKey, timeZone],
  );

  useEffect(() => {
    let timer;
    const wakeAt = (atMs, then) => {
      timer = setTimeout(
        then,
        Math.min(Math.max(atMs - now(), 0), MAX_WAIT_MS),
      );
    };

    const plan = () => {
      const at = now();
      const current = slots.find((s) => at < s.endMs);
      if (!current) {
        setState((draft) => {
          draft.activeItem = IDLE;
        });
        // Nothing left today: the day is re-evaluated (and rolls over) by the bundle hook.
        return;
      }
      if (at < current.startMs) {
        setState((draft) => {
          draft.activeItem = IDLE;
        });
        wakeAt(current.startMs, plan);
        return;
      }

      const { playlist } = current.slot;
      const items = playableItems(playlist, { online: true, dayKey });
      const position = timelineAt(
        items.map((item) => wallItemSeconds(item, playlist) * 1000),
        current.startMs,
        at,
      );
      setState((draft) => {
        draft.activeItem = current.slot;
        draft.index = position?.index ?? 0;
        draft.itemStartedAtMs = position?.itemStartMs ?? current.startMs;
      });

      const slotEnded = () => {
        setState((draft) => {
          draft.index = 0;
        });
        onSlotEndedRef.current?.();
        plan();
      };
      const nextChange = position?.nextChangeAtMs ?? Infinity;
      if (nextChange < current.endMs) wakeAt(nextChange, plan);
      else
        wakeAt(current.endMs, () =>
          now() >= current.endMs ? slotEnded() : plan(),
        );
    };

    plan();
    return () => clearTimeout(timer);
  }, [slots, now]);

  const playlist = state.activeItem.playlist;
  const items = useMemo(
    () =>
      state.activeItem.scheduleId > 0
        ? playableItems(playlist, { online: true, dayKey })
        : NO_ITEMS,
    [state.activeItem, dayKey],
  );
  const index = items.length ? state.index % items.length : 0;

  return {
    activeItem: state.activeItem,
    currentIndex: index,
    items,
    currentItem: items[index],
    // The timeline decides when items change; a video ending changes nothing.
    advance: NOOP,
    itemStartedAtMs: state.itemStartedAtMs,
  };
};
